import { inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Component } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { SortEvent } from 'primeng/api';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  Subscription,
  take,
  TeardownLogic,
} from 'rxjs';
import { Params } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { Location } from '@angular/common';
import { TablePageEvent } from 'primeng/table';
import { PaginatorState } from 'primeng/paginator';

// btoa/atob supportano solo Latin1: i filtri avanzati (es. nome di una commessa
// con caratteri accentati o simboli tipografici) vanno codificati/decodificati
// passando prima per UTF-8, altrimenti btoa lancia InvalidCharacterError.
function toUnicodeBase64(str: string): string {
  return btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    )
  );
}

function fromUnicodeBase64(str: string): string {
  return decodeURIComponent(
    atob(str)
      .split('')
      .map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
      .join('')
  );
}

export interface FrmAdvSearchConfig {
  form_control_name: string;
  type: 'object' | 'date' | 'date-range' | 'object-array' | 'array' | 'array-numbers' | 'string';
  keys?: string[];
}

@Component({ template: '' })
export abstract class AdvancedListComponent<T> {
  protected readonly ar = inject(ActivatedRoute);
  protected readonly router = inject(Router);

  protected readonly title = inject(Title);

  protected readonly location = inject(Location);
  protected readonly rowsPerPageOptions = [25, 50, 100, 200, 500];
  environment = environment;

  protected subs = new Subscription();

  protected advancedAccordionExpanded: number | undefined = undefined;

  /**
   * Per usare ListComponent, bisogna specificare il tipo che verrà usato nella Tabella
   * ES: Se lavoro con UserListResponse, probabilmente il generic da usare sarà UserItem o UserItemMinimal
   */
  public TableType!: T;
  public readonly ITEMS_PER_PAGE: number = 25;
  abstract frm_search: FormGroup;
  abstract sorting: { field: string; direction: number };
  abstract cols: { field: string; header: string; sort: boolean }[];
  frm_adv_search: FormGroup | undefined = undefined;
  frm_adv_search_objects: FrmAdvSearchConfig[] | undefined = undefined;
  ignoreArParams: string[] = [];
  emitEventOnFrmSearch = true;
  abstract list_id: string;
  abstract fetch(): void;
  private last_query_search_param?: string; // to handle pagination reset

  private get storageKey(): string {
    return `filters_${this.router.url.split('?')[0]}`;
  }

  /** Toglie i parametri vuoti: un filtro svuotato dall'utente non deve restare nell'indirizzo né nello storage. */
  private withoutEmpty(params: Params): Params {
    const result: Params = {};
    for (const k of Object.keys(params)) {
      const v = params[k];
      if (v !== '' && v !== null && v !== undefined) {
        result[k] = v;
      }
    }
    return result;
  }

  private saveFiltersToStorage() {
    const params = this.withoutEmpty(this.ar.snapshot.queryParams);
    if (Object.keys(params).length > 0) {
      sessionStorage.setItem(this.storageKey, JSON.stringify(params));
    }
  }

  private restoreFiltersFromStorage(): Params | null {
    const saved = sessionStorage.getItem(this.storageKey);
    return saved ? this.withoutEmpty(JSON.parse(saved)) : null;
  }

  private clearFiltersFromStorage() {
    sessionStorage.removeItem(this.storageKey);
  }

  back() {
    this.location.back();
  }

  observe(teardown: TeardownLogic) {
    this.subs.add(teardown);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  onAuxClick(event: MouseEvent, urlTree: string[]) {
    if (event.button === 0 || event.button === 1) {
      event.preventDefault();
      const url = this.router.serializeUrl(this.router.createUrlTree(urlTree));
      window.open(url, '_blank');
      return false;
    }
    return true;
  }

  // Valori di default dei form di ricerca/filtri al momento
  // dell'inizializzazione (prima di qualunque patch da query string). Usati
  // per due scopi:
  // 1) distinguere, nella query string, un filtro realmente impostato
  //    dall'utente da un controllo che ha semplicemente un valore non vuoto
  //    di default (es. un booleano "true" di serie) — altrimenti
  //    quest'ultimo farebbe riaprire l'accordion dei filtri ad ogni
  //    navigazione, incluso un semplice click su un'intestazione ordinabile;
  // 2) sapere quali campi sono dichiarati come stringa, per non convertirli
  //    in number quando il loro contenuto è numerico (es. un filtro
  //    testuale su partita IVA, telefono o codice prodotto): senza questa
  //    informazione, il parsing generico della query string qui sotto li
  //    scambierebbe per campi numerici e il valore verrebbe perso al primo
  //    giro di navigazione (es. un click su un'intestazione ordinabile).
  private formFieldDefaults: Record<string, any> = {};

  ngOnInit() {
    this.formFieldDefaults = {
      ...this.frm_search.value,
      ...(this.frm_adv_search ? this.frm_adv_search.value : {}),
    };

    const currentParams = this.ar.snapshot.queryParams;
    const hasParams = Object.keys(currentParams).length > 0;

    this.ar.queryParams.subscribe(async query => {
      // @TODO check if only my own fields are changed
      let q = { ...query };
      if (this.list_id) {
        for (let k in q) {
          q[k.replace(`${this.list_id}_`, '')] = q[k];
          delete q[k];
        }
      }
      let skip = this.frm_search.get('skip')?.value || 0;
      if (query['query']) {
        if (this.last_query_search_param != query['query']) {
          skip = 0;
        }
        this.last_query_search_param = query['query'];
      }
      let params: { [key: string]: any } = { ...q, skip }; // @BUG!!!!!!! reset skip when params change
      if (params[`field`]) {
        this.sorting.field = params['field'];
      }
      if (params['direction']) {
        this.sorting.direction = +params['direction'];
      }
      for (const k in q) {
        if (this.ignoreArParams.includes(k)) {
          continue;
        }
        // boolean
        if (q[k] && (q[k] === 'false' || q[k] === 'true')) {
          params[k] = q[k] === 'true';
        }
        // number (mai per un campo dichiarato o attualmente valorizzato come
        // stringa: altrimenti un filtro testuale con contenuto numerico, es.
        // partita IVA/telefono/codice prodotto, verrebbe convertito in
        // number e la richiesta al BE, che si aspetta una stringa,
        // perderebbe il filtro). Il valore "live" del controllo copre anche
        // i campi il cui default è undefined ma che in questo momento
        // contengono il testo appena digitato dall'utente (caso tipico:
        // filtro testuale poi click su un'intestazione ordinabile, nella
        // stessa sessione, senza reload di pagina).
        const liveValue = this.frm_search.get(k)?.value ?? this.frm_adv_search?.get(k)?.value;
        const isString =
          typeof this.formFieldDefaults[k] === 'string' || typeof liveValue === 'string';
        if (
          !isString &&
          !q[k].includes('e') &&
          q[k] &&
          !isNaN(+q[k]) &&
          !Array.isArray(q[k])
        ) {
          params[k] = +q[k];
        }
        // array of numbers
        if (q[k] && Array.isArray(q[k]) && !isNaN(+q[k][0])) {
          params[k] = q[k].map((x: any) => +x);
        }
        // objects
        if (this.frm_adv_search_objects) {
          const foundConfig = this.frm_adv_search_objects.find(f => f.form_control_name === k);
          if (foundConfig) {
            try {
              switch (foundConfig.type) {
                case 'array':
                  params[k] = Array.isArray(q[k]) ? q[k] : [q[k]];
                  break;
                case 'array-numbers':
                  params[k] = (Array.isArray(q[k]) ? q[k] : [q[k]]).map((x: string) => +x);
                  break;
                case 'date':
                  params[k] = new Date(q[k]);
                  break;
                case 'string':
                  params[k] = q[k];
                  break;
                case 'date-range':
                  params[k] = (JSON.parse(fromUnicodeBase64(q[k])) as string[]).map(dateString =>
                    dateString ? new Date(dateString) : null
                  );
                  break;
                case 'object':
                  params[k] = JSON.parse(fromUnicodeBase64(q[k]));
                  break;
                case 'object-array':
                  params[k] = JSON.parse(fromUnicodeBase64(q[k]));
                  if (params[k].length === 0) {
                    delete params[k];
                  }
                  break;
              }
            } catch (error) {}
          }
        }
      }

      this.frm_search.patchValue(params, { emitEvent: this.emitEventOnFrmSearch });
      if (this.querystringHasAdvSearchParams(params)) {
        this.advancedAccordionExpanded = 0;
        this.toggleAdvSearch(new Event('click'), true);
        this.frm_adv_search?.patchValue(params, { emitEvent: this.emitEventOnFrmSearch });
      } else {
        this.advancedAccordionExpanded = undefined;
      }

      this.saveFiltersToStorage();
      this.fetch();

      if (this.frm_search.get('query')) {
        this.frm_search
          .get('query')!
          .valueChanges.pipe(debounceTime(300), distinctUntilChanged())
          .subscribe(v => {
            const queryParams: Params = { ...this.ar.snapshot.queryParams, skip: 0 };
            queryParams[this.list_id ? this.list_id + '_query' : 'query'] = v;
            if (v) {
              this.router.navigate(['.'], {
                relativeTo: this.ar,
                queryParams,
              });
            } else {
              // this.fetch();
              // remove all queryparams
              for (let k in queryParams) {
                if (k.includes(this.list_id)) {
                  delete queryParams[k];
                }
              }
              this.router.navigate(['.'], {
                relativeTo: this.ar,
                queryParams,
              });
            }
          });
      }
    });

    if (!hasParams) {
      const saved = this.restoreFiltersFromStorage();
      if (saved) {
        this.subs.unsubscribe();
        this.subs = new Subscription();
        this.router.navigate(['.'], {
          relativeTo: this.ar,
          queryParams: saved,
          replaceUrl: true,
        });
      }
    }
  }

  onPageChange(e: PaginatorState) {
    this.router.navigate(['.'], {
      relativeTo: this.ar,
      queryParams: {
        ...this.ar.snapshot.queryParams,
        skip: e.first!,
        pageSize: e.rows,
      },
    });
  }

  sort(e: SortEvent) {
    if (this.sorting.field == (e.field! as any) && this.sorting.direction == e.order!) {
      return;
    }
    this.sorting = { field: e.field! as any, direction: e.order! };
    this.updateParams();
  }

  updateParams() {
    let queryParams = this.frm_search.value;
    for (const k in queryParams) {
      if (this.ignoreArParams.includes(k)) {
        delete queryParams[k];
      }
    }
    this.router.navigate(['.'], {
      relativeTo: this.ar,
      queryParams: {
        ...this.frm_search.value,
        ...this.sorting,
        // i filtri a oggetto/intervallo vanno codificati, come in advSearch(): messi così
        // finirebbero nell'indirizzo come "[object Object]" e l'ordinamento li farebbe perdere
        ...(this.frm_adv_search ? this.processAdvSearchParams() : {}),
      },
    });
  }

  advSearch() {
    if (Object.values(this.frm_adv_search?.value).find(v => !!v)) {
      this.router.navigate(['.'], {
        relativeTo: this.ar,
        // senza i filtri svuotati: altrimenti restano nell'indirizzo come "chiave=" e finiscono nello storage
        queryParams: this.withoutEmpty({
          skip: 0,
          pageSize: this.frm_search.value.pageSize,
          ...(this.sorting.field && this.sorting.direction
            ? { field: this.sorting.field, direction: this.sorting.direction }
            : {}),
          ...this.processAdvSearchParams(),
        }),
      });
    } else {
      // Tutti i filtri avanzati sono stati tolti e l'URL resta senza parametri. Lo storage va svuotato:
      // saveFiltersToStorage() non salva mai uno stato vuoto, quindi altrimenti terrebbe i filtri
      // vecchi e alla prossima apertura della lista (URL senza parametri) verrebbero ripristinati.
      this.clearFiltersFromStorage();
      this.fetch();
      this.router.navigate(['.'], { relativeTo: this.ar });
    }
  }

  processAdvSearchParams() {
    if (!this.frm_adv_search_objects) {
      return { ...this.frm_adv_search!.value };
    } else {
      // copia: i valori vengono sostituiti dalla loro forma codificata e non devono toccare il form
      const x = { ...this.frm_adv_search!.value };
      for (let o of this.frm_adv_search_objects) {
        if (!x[o.form_control_name]) {
          continue;
        }
        switch (o.type) {
          case 'date':
            x[o.form_control_name] = (x[o.form_control_name] as Date).toISOString();
            break;
          case 'date-range':
            const dates = x[o.form_control_name] as Date[];
            if (dates.length > 1) {
              x[o.form_control_name] = toUnicodeBase64(
                JSON.stringify((x[o.form_control_name] as Date[]).map(x => x?.toISOString()))
              );
            } else if (dates.length === 1) {
              x[o.form_control_name] = toUnicodeBase64(
                JSON.stringify(
                  (x[o.form_control_name] as Date[]).map(x => x?.toISOString() || null)
                )
              );
            }
            break;
          case 'object': {
            const result = o.keys!.reduce(
              (acc, key) => ({ ...acc, [key]: x[o.form_control_name][key] }),
              {} as Record<string, any>
            );
            x[o.form_control_name] = toUnicodeBase64(JSON.stringify(result));
            break;
          }
          case 'object-array': {
            const result: Record<string, any>[] = (x[o.form_control_name] as any[]).reduce(
              (acc, el) => {
                const result = o.keys!.reduce(
                  (acc, key) => ({ ...acc, [key]: el[key] }),
                  {} as Record<string, any>
                );
                return [...acc, result];
              },
              [] as Record<string, any>[]
            );
            if (result.length > 0) {
              x[o.form_control_name] = toUnicodeBase64(JSON.stringify(result));
            } else {
              delete x[o.form_control_name];
            }
            break;
          }
        }
      }
      return x;
    }
  }

  querystringHasAdvSearchParams(queryparams: { [key: string]: any }) {
    if (!this.frm_adv_search) {
      return false;
    }
    const advSearchKeys = Object.keys(this.frm_adv_search.value);
    // Un ordinamento (field/direction) aggiorna la query string riportando anche
    // i filtri correnti (per non perderli al refresh), ma questo non deve da
    // solo far scattare la riapertura automatica dell'accordion dei filtri: va
    // considerato un parametro "presente" solo se ha un valore reale E diverso
    // dal default del relativo controllo (es. un booleano true "di serie" non
    // conta come filtro attivo, altrimenti riaprirebbe l'accordion ad ogni
    // navigazione, incluso un semplice click su un'intestazione ordinabile).
    return Object.keys(queryparams).some(k => {
      if (!advSearchKeys.includes(k)) {
        return false;
      }
      const v = queryparams[k];
      if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
        return false;
      }
      const def = this.formFieldDefaults[k];
      if (def === undefined || def === null || def === '' || Array.isArray(def)) {
        return true;
      }
      return String(v) !== String(def);
    });
  }

  toggleAdvSearch(e: Event, value: boolean): void {
    e.preventDefault();
    if (value === false) {
      this.frm_adv_search?.reset();
      this.fetch();
      this.router.navigate(['.'], { relativeTo: this.ar });
    }
  }

  clearFilter() {
    this.clearFiltersFromStorage(); // pulisce anche lo storage
    this.frm_adv_search?.reset();
    this.frm_search?.reset({
      skip: 0,
      pageSize: this.ITEMS_PER_PAGE,
      ...(this.sorting.field && this.sorting.direction
        ? { field: this.sorting.field, direction: this.sorting.direction }
        : {}),
      query: undefined,
    });
  }
}
